import * as Slider from "@radix-ui/react-slider";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type VariantProps, tv } from "tailwind-variants";
import TooltipProvider from "../../core/tooltip/TooltipProvider";

const RangeSliderStyles = tv({
  slots: {
    Root: "relative flex h-3 w-full min-w-[32px] touch-none select-none items-center justify-center",
    Track:
      "bg-action-secondary-default flex-grow-1 relative z-0 h-2 w-full rounded-full",
    Range:
      "bg-surface-highlight-subdued-hovered -z-1 absolute h-full rounded-full",
    Thumb:
      "focus-visible:ring-focused-black focus-visible:bg-action-primary-default z-20 block h-5 w-5 cursor-grab rounded-full border border-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-opacity-50 active:cursor-grabbing",
    Discrete:
      "block h-2 w-2 cursor-pointer rounded-full border-2 bg-black opacity-10 hover:opacity-20",
  },
  variants: {
    value: {
      undefined: {
        Thumb: "hover:bg-action-primary-hovered bg-action-tertiary-depressed",
      },
      defined: {
        Thumb: "hover:bg-action-primary-hovered bg-action-primary-default",
      },
    },
  },
});
interface RangeSliderProps
  extends Slider.SliderProps,
    VariantProps<typeof RangeSliderStyles> {
  range?: boolean;
  labels?: string[];
  discrete?: boolean;
  allowClick?: boolean;
  ariaLabeledby?: string;
  ariaDescribedby?: string;
}

const SliderToolTip = ({
  labels,
  index,
  children,
  open,
}: {
  labels: string[];
  index: number;
  children: React.ReactNode;
  open?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <TooltipProvider
      primaryContent={
        labels?.[index] ?? t("util:select", { val: t("global:please") })
      }
      delay={100}
      open={open}
    >
      {children}
    </TooltipProvider>
  );
};
const DiscreteSteps = ({ labels, max, min, step }) => {
  const { Discrete } = RangeSliderStyles();
  return (
    <div className="absolute z-10 flex h-2 w-full items-center justify-between px-2">
      {[...Array(Math.ceil((max - min) / step) + 1).keys()].map((e, i) => (
        <SliderToolTip labels={labels} index={i} key={e}>
          <div className={Discrete()} />
        </SliderToolTip>
      ))}
    </div>
  );
};
const RangeSlider = ({
  value,
  labels,
  className,
  discrete = false,
  range = false,
  max = 100,
  min = 0,
  step = 1,
  ariaLabeledby,
  ariaDescribedby,
  ...props
}: RangeSliderProps) => {
  const { Root, Track, Range, Thumb } = RangeSliderStyles({
    value: value === undefined ? "undefined" : "defined",
  });
  const defaultIndex = (max - min) / 2 + min;
  const onClick = () => {
    if (!((max - min) % 2) && value === undefined)
      props.onValueChange?.([defaultIndex]);
  };
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current?.matches(":has(:focus-visible)")) setIsFocused(true);
  }, [value]);
  const [isFocused, setIsFocused] = useState(false);
  return (
    <Slider.Root
      defaultValue={[defaultIndex]}
      value={value}
      max={max}
      min={min}
      step={step}
      {...props}
      className={Root({ className })}
      ref={ref}
    >
      <Slider.Track className={Track()}>
        {discrete && (
          <DiscreteSteps max={max} min={min} step={step} labels={labels} />
        )}
        {range && <Slider.Range className={Range()} />}
      </Slider.Track>
      <Slider.Thumb
        className={Thumb()}
        onClick={onClick}
        onFocus={(e) => {
          setIsFocused(e.currentTarget.matches(":focus-visible"));
        }}
        onBlur={() => setIsFocused(false)}
        aria-labelledby={ariaLabeledby}
        aria-describedby={ariaDescribedby}
      >
        {labels && (
          <SliderToolTip
            labels={labels}
            index={(value?.[0] ?? 0) - min}
            open={isFocused}
          >
            <div className="h-5 w-5 opacity-0" />
          </SliderToolTip>
        )}
      </Slider.Thumb>
    </Slider.Root>
  );
};

export default RangeSlider;
